import React, { useEffect, useMemo, useState } from 'react';
import { Container, Dropdown, DropdownMenu, DropdownToggle } from 'reactstrap';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import {
    Block,
    Heading3,
    TextDefault,
    TextTiny,
    ThemedButton,
    FlexDiv,
    palette,
} from '@awarego-ui/components';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderText from '../../components/header/SubHeaderText';
import SubHeaderComponentsList from '../../components/header/SubHeaderComponentsList';
import { ThemedDropdownItem } from '../../components/themed/ThemedComponents';
import { useStores } from '../../hooks/use-stores';
import AwarePhishSubNav from './AwarePhishSubNav';

const FieldLabel = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${palette.graphite.charcoal};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 12px 0 6px;
`;

const StatusPill = styled.span`
    display: inline-block;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    background: ${(p) =>
        p.$tone === 'ok'
            ? palette.pastel.aspargus
            : p.$tone === 'warn'
              ? palette.pastel.orange
              : palette.graphite.cloud};
    color: ${(p) =>
        p.$tone === 'ok'
            ? palette.evergreen.aspargus
            : p.$tone === 'warn'
              ? palette.vibrant.coral
              : palette.graphite.charcoal};
`;

const StepCard = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    border: 1px solid ${palette.graphite.silver};
    border-radius: 6px;
    padding: 12px 14px;
    margin-bottom: 10px;
`;

const StepTitle = styled(TextDefault)`
    display: block;
    font-weight: 700;
`;

const StepDetail = styled(TextTiny)`
    display: block;
    line-height: 1.45;
`;

const ValueRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 10px 0;
    border-bottom: 1px solid ${palette.graphite.silver};
    &:last-child {
        border-bottom: none;
    }
`;

const ValuesBox = styled.div`
    border: 1px solid ${palette.graphite.silver};
    border-radius: 6px;
    padding: 4px 14px;
    margin: 8px 0 14px;
    background: ${palette.graphite.cloud};
`;

const Select = styled.select`
    width: 100%;
    padding: 8px;
    font-size: 13px;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    th,
    td {
        text-align: left;
        padding: 10px 8px;
        border-bottom: 1px solid ${palette.graphite.silver};
        vertical-align: top;
    }
    th {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: ${palette.graphite.steel};
    }
    tr {
        cursor: pointer;
    }
    tr:hover td {
        background: ${palette.graphite.cloud};
    }
`;

const SectionIntro = styled(TextTiny)`
    display: block;
    margin-bottom: 12px;
    line-height: 1.45;
`;

function setupTone(status) {
    if (status === 'ready') return 'ok';
    if (status === 'action_required' || status === 'needs_setup') return 'warn';
    return 'neutral';
}

function setupLabel(status) {
    if (status === 'ready') return 'Ready';
    if (status === 'action_required') return 'Action required';
    if (status === 'needs_setup') return 'Needs setup';
    return status || '—';
}

function AwarePhishDelivery() {
    const { store, awarePhishStore, commonStore } = useStores();
    const { currentCompany } = store;
    const companyId = currentCompany && currentCompany.company_id;
    const {
        delivery,
        deliveryProfiles,
        loadingDelivery,
        loadingDeliveryProfiles,
        savingAsset,
    } = awarePhishStore;

    const [selectedId, setSelectedId] = useState(null);
    const [mailPlatform, setMailPlatform] = useState('m365');
    const [deliveryStatus, setDeliveryStatus] = useState('not_started');
    const [reportingStatus, setReportingStatus] = useState('not_started');
    const [reportingMailbox, setReportingMailbox] = useState('');
    const [showCreate, setShowCreate] = useState(false);
    const [addMenuOpen, setAddMenuOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [newSenderId, setNewSenderId] = useState('');
    const [newLandingId, setNewLandingId] = useState('');

    const reload = async () => {
        if (!companyId) return;
        await Promise.all([
            awarePhishStore.loadDelivery(companyId),
            awarePhishStore.loadDeliveryProfiles(companyId),
        ]);
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId]);

    useEffect(() => {
        if (!delivery) return;
        setMailPlatform(delivery.mailPlatform || 'm365');
        setDeliveryStatus(delivery.deliveryStatus || 'not_started');
        setReportingStatus(delivery.reportingStatus || 'not_started');
        setReportingMailbox(delivery.reportingMailbox || '');
    }, [delivery]);

    useEffect(() => {
        if (!selectedId && deliveryProfiles.length) {
            setSelectedId(deliveryProfiles[0].id);
        }
    }, [deliveryProfiles, selectedId]);

    const selected = useMemo(
        () => deliveryProfiles.find((p) => p.id === selectedId) || null,
        [deliveryProfiles, selectedId]
    );

    const senders = (delivery && delivery.availableSenders) || [];
    const landings = (delivery && delivery.availableLandings) || [];
    const instructions = selected && selected.instructions;
    const guideSteps =
        (instructions && instructions.steps) ||
        (selected && selected.nextSteps) ||
        [];

    const saveTenant = async (extra = {}) => {
        try {
            await awarePhishStore.saveDelivery(companyId, {
                mailPlatform,
                deliveryStatus,
                reportingStatus,
                reportingMailbox: reportingMailbox || null,
                markInstructed: true,
                ...extra,
            });
            commonStore.success('Delivery settings saved');
            await reload();
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Failed to save delivery settings'
            );
        }
    };

    const createCustom = async () => {
        try {
            const p = await awarePhishStore.saveDeliveryProfile(
                companyId,
                null,
                {
                    name: newName,
                    senderDomainId: newSenderId,
                    landingDomainId: newLandingId,
                }
            );
            commonStore.success('Delivery profile created');
            setShowCreate(false);
            setNewName('');
            setNewSenderId('');
            setNewLandingId('');
            if (p && p.id) setSelectedId(p.id);
        } catch (e) {
            commonStore.error(
                awarePhishStore.error || 'Failed to create profile'
            );
        }
    };

    return (
        <Container className="manager-content">
            <Helmet>
                <title>AwarePHISH delivery</title>
            </Helmet>
            <HeaderSection
                heading="Delivery"
                subHeadings={
                    <SubHeaderComponentsList>
                        <SubHeaderText
                            text={
                                'Before you run a simulation, allow AwarePHISH mail through your filters so messages land in Inbox—not quarantine. Pick a profile, follow the steps, then create a campaign.'
                            }
                        />
                    </SubHeaderComponentsList>
                }
            />
            <AwarePhishSubNav active="delivery" />

            {(loadingDelivery || loadingDeliveryProfiles) &&
            !deliveryProfiles.length ? (
                <TextDefault lighter>Loading…</TextDefault>
            ) : (
                <>
                    <Block padding="20" column style={{ marginBottom: 14 }}>
                        <FlexDiv
                            alignCenter
                            justifyBetween
                            style={{ marginBottom: 8 }}
                        >
                            <div>
                                <Heading3>Profiles</Heading3>
                                <SectionIntro lighter>
                                    A profile pairs a sender domain with a
                                    landing page. Built-in profiles use matching
                                    AwareGO domains. Choose one when you create
                                    a simulation.
                                </SectionIntro>
                            </div>
                            <Dropdown
                                isOpen={addMenuOpen}
                                toggle={() => setAddMenuOpen((v) => !v)}
                            >
                                <DropdownToggle
                                    caret
                                    tag="button"
                                    type="button"
                                    className="btn"
                                    style={{
                                        background: palette.pastel.aspargus,
                                        color: palette.evergreen.aspargus,
                                        border: 'none',
                                        fontWeight: 600,
                                        fontSize: 13,
                                        padding: '8px 14px',
                                    }}
                                >
                                    Add custom
                                </DropdownToggle>
                                <DropdownMenu end>
                                    <ThemedDropdownItem
                                        onClick={() => {
                                            setShowCreate(true);
                                            setAddMenuOpen(false);
                                        }}
                                    >
                                        Create custom profile…
                                    </ThemedDropdownItem>
                                </DropdownMenu>
                            </Dropdown>
                        </FlexDiv>

                        {showCreate ? (
                            <div
                                style={{
                                    marginBottom: 16,
                                    padding: 14,
                                    border: `1px solid ${palette.graphite.silver}`,
                                    borderRadius: 6,
                                }}
                            >
                                <TextDefault bold style={{ display: 'block' }}>
                                    Create custom profile
                                </TextDefault>
                                <SectionIntro lighter>
                                    Mix any ready sender domain with any ready
                                    landing domain when a built-in pair is not
                                    enough.
                                </SectionIntro>
                                <FieldLabel>Name</FieldLabel>
                                <input
                                    style={{
                                        width: '100%',
                                        padding: 8,
                                        fontSize: 13,
                                        boxSizing: 'border-box',
                                    }}
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    placeholder="e.g. Cross-domain pilot"
                                />
                                <FieldLabel>Sender domain</FieldLabel>
                                <Select
                                    value={newSenderId}
                                    onChange={(e) =>
                                        setNewSenderId(e.target.value)
                                    }
                                >
                                    <option value="">Select…</option>
                                    {senders.map((d) => (
                                        <option key={d.id} value={d.id}>
                                            {d.hostname}
                                        </option>
                                    ))}
                                </Select>
                                <FieldLabel>Landing domain</FieldLabel>
                                <Select
                                    value={newLandingId}
                                    onChange={(e) =>
                                        setNewLandingId(e.target.value)
                                    }
                                >
                                    <option value="">Select…</option>
                                    {landings.map((d) => (
                                        <option key={d.id} value={d.id}>
                                            {d.hostname}
                                        </option>
                                    ))}
                                </Select>
                                <FlexDiv gap={12} style={{ marginTop: 12 }}>
                                    <ThemedButton
                                        text={
                                            savingAsset
                                                ? 'Saving…'
                                                : 'Create profile'
                                        }
                                        mode="primary"
                                        disabled={
                                            savingAsset ||
                                            !newName.trim() ||
                                            !newSenderId ||
                                            !newLandingId
                                        }
                                        onClick={createCustom}
                                        $noWrap
                                    />
                                    <ThemedButton
                                        text="Cancel"
                                        mode="secondary"
                                        onClick={() => setShowCreate(false)}
                                        $noWrap
                                    />
                                </FlexDiv>
                            </div>
                        ) : null}

                        {!deliveryProfiles.length ? (
                            <TextTiny lighter>
                                No delivery profiles yet. Built-in AwareGO
                                profiles appear once sender and landing domains
                                are ready.
                            </TextTiny>
                        ) : (
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Profile</th>
                                        <th>Sender</th>
                                        <th>Landing</th>
                                        <th>Type</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deliveryProfiles.map((p) => (
                                        <tr
                                            key={p.id}
                                            onClick={() => setSelectedId(p.id)}
                                            style={{
                                                background:
                                                    selectedId === p.id
                                                        ? palette.graphite.cloud
                                                        : undefined,
                                            }}
                                        >
                                            <td>
                                                <TextDefault bold>
                                                    {p.name}
                                                </TextDefault>
                                            </td>
                                            <td>
                                                {(p.senderDomain &&
                                                    p.senderDomain.hostname) ||
                                                    '—'}
                                            </td>
                                            <td>
                                                {(p.landingDomain &&
                                                    p.landingDomain.hostname) ||
                                                    '—'}
                                            </td>
                                            <td>
                                                {p.isBuiltin
                                                    ? 'Built-in'
                                                    : 'Custom'}
                                            </td>
                                            <td>
                                                <StatusPill
                                                    $tone={setupTone(
                                                        p.setupStatus
                                                    )}
                                                >
                                                    {setupLabel(p.setupStatus)}
                                                </StatusPill>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Block>

                    {selected ? (
                        <Block padding="20" column style={{ marginBottom: 14 }}>
                            <FlexDiv
                                alignCenter
                                justifyBetween
                                style={{ marginBottom: 4 }}
                            >
                                <Heading3>{selected.name}</Heading3>
                                <StatusPill
                                    $tone={setupTone(selected.setupStatus)}
                                >
                                    {setupLabel(selected.setupStatus)}
                                </StatusPill>
                            </FlexDiv>
                            <SectionIntro lighter>
                                Sender:{' '}
                                {(selected.senderDomain &&
                                    selected.senderDomain.hostname) ||
                                    '—'}
                                {' · '}
                                Landing: {selected.landingUrl || '—'}
                                {' · '}
                                {selected.isBuiltin ? 'Built-in' : 'Custom'}
                            </SectionIntro>

                            {(selected.blockers || []).length ? (
                                <div style={{ marginBottom: 8 }}>
                                    <FieldLabel>
                                        Waiting before you can finish
                                    </FieldLabel>
                                    {(selected.blockers || []).map((b) => (
                                        <StepCard key={b.id}>
                                            <StepTitle>{b.title}</StepTitle>
                                            <StepDetail lighter>
                                                {b.detail}
                                            </StepDetail>
                                        </StepCard>
                                    ))}
                                </div>
                            ) : null}

                            {instructions ? (
                                <>
                                    <Heading3>
                                        {instructions.title ||
                                            'Setup instructions'}
                                    </Heading3>
                                    {instructions.why ? (
                                        <SectionIntro lighter>
                                            {instructions.why}
                                        </SectionIntro>
                                    ) : (
                                        <SectionIntro lighter>
                                            Follow these steps so simulation
                                            mail from this profile reaches
                                            Inbox.
                                        </SectionIntro>
                                    )}

                                    <FieldLabel>
                                        Values to enter in your mail system
                                    </FieldLabel>
                                    <ValuesBox>
                                        <ValueRow>
                                            <TextTiny lighter>
                                                Domain (trusted sending name or
                                                sender)
                                            </TextTiny>
                                            <TextDefault bold>
                                                {instructions.dkimDomain ||
                                                    instructions.senderDomain ||
                                                    '—'}
                                            </TextDefault>
                                        </ValueRow>
                                        <ValueRow>
                                            <TextTiny lighter>
                                                Sending IP(s)
                                            </TextTiny>
                                            <TextDefault bold>
                                                {(
                                                    instructions.sendingIps ||
                                                    []
                                                ).join(', ') || '—'}
                                            </TextDefault>
                                        </ValueRow>
                                        <ValueRow>
                                            <TextTiny lighter>
                                                Landing page
                                            </TextTiny>
                                            <TextDefault bold>
                                                {instructions.landingUrl ||
                                                    '—'}
                                            </TextDefault>
                                        </ValueRow>
                                    </ValuesBox>
                                </>
                            ) : (
                                <SectionIntro lighter>
                                    Follow the steps below so simulation mail
                                    from this profile reaches Inbox.
                                </SectionIntro>
                            )}

                            {guideSteps.length ? (
                                <div>
                                    <FieldLabel>Steps</FieldLabel>
                                    {guideSteps.map((s, index) => (
                                        <StepCard key={s.id || index}>
                                            <StepTitle>
                                                {index + 1}. {s.title}
                                            </StepTitle>
                                            <StepDetail lighter>
                                                {s.detail}
                                            </StepDetail>
                                        </StepCard>
                                    ))}
                                </div>
                            ) : null}

                            {!selected.isBuiltin ? (
                                <FlexDiv gap={12} style={{ marginTop: 12 }}>
                                    <ThemedButton
                                        text="Delete custom profile"
                                        mode="secondary"
                                        disabled={savingAsset}
                                        onClick={async () => {
                                            try {
                                                await awarePhishStore.removeDeliveryProfile(
                                                    companyId,
                                                    selected.id
                                                );
                                                setSelectedId(null);
                                                commonStore.success(
                                                    'Profile deleted'
                                                );
                                            } catch (e) {
                                                commonStore.error(
                                                    awarePhishStore.error ||
                                                        'Delete failed'
                                                );
                                            }
                                        }}
                                        $noWrap
                                    />
                                </FlexDiv>
                            ) : null}
                        </Block>
                    ) : null}

                    <Block padding="20" column style={{ marginBottom: 14 }}>
                        <Heading3>Your mail system</Heading3>
                        <SectionIntro lighter>
                            Shared settings for every profile. Tell us which
                            mail platform you use so instructions match, track
                            whether allowlisting is done, and optionally set a
                            mailbox where employees forward reported phishing.
                        </SectionIntro>

                        <FieldLabel>Mail platform</FieldLabel>
                        <Select
                            value={mailPlatform}
                            onChange={(e) => setMailPlatform(e.target.value)}
                        >
                            <option value="m365">Microsoft 365</option>
                            <option value="google">Google Workspace</option>
                            <option value="other">Other / on-prem</option>
                        </Select>

                        <FieldLabel>Allowlist progress</FieldLabel>
                        <Select
                            value={deliveryStatus}
                            onChange={(e) => setDeliveryStatus(e.target.value)}
                        >
                            <option value="not_started">Not started</option>
                            <option value="instructed">Instructions shared</option>
                            <option value="configured">Configured in mail system</option>
                            <option value="verified">Verified (Inbox OK)</option>
                            <option value="failed">Failed</option>
                        </Select>

                        <FieldLabel>Employee reporting progress</FieldLabel>
                        <Select
                            value={reportingStatus}
                            onChange={(e) => setReportingStatus(e.target.value)}
                        >
                            <option value="not_started">Not started</option>
                            <option value="instructed">Instructions shared</option>
                            <option value="configured">Configured</option>
                            <option value="verified">Verified</option>
                            <option value="failed">Failed</option>
                        </Select>

                        <FieldLabel>
                            Reporting mailbox (optional, Microsoft 365)
                        </FieldLabel>
                        <SectionIntro lighter style={{ marginTop: -4 }}>
                            If employees report phishing to a shared mailbox,
                            AwarePHISH can read it and mark matching simulation
                            recipients as Reported.
                        </SectionIntro>
                        <input
                            style={{
                                width: '100%',
                                padding: 8,
                                fontSize: 13,
                                boxSizing: 'border-box',
                            }}
                            value={reportingMailbox}
                            onChange={(e) =>
                                setReportingMailbox(e.target.value)
                            }
                            placeholder="phishing-reports@yourcompany.com"
                        />

                        <FlexDiv gap={12} style={{ marginTop: 16 }} $noWrap>
                            <ThemedButton
                                text={savingAsset ? 'Saving…' : 'Save'}
                                mode="primary"
                                onClick={() => saveTenant()}
                                disabled={savingAsset}
                                $noWrap
                            />
                            <ThemedButton
                                text="Mark delivery verified"
                                mode="secondary"
                                onClick={() =>
                                    saveTenant({ deliveryStatus: 'verified' })
                                }
                                disabled={savingAsset}
                                $noWrap
                            />
                        </FlexDiv>
                    </Block>

                    <Block padding="20" column style={{ marginBottom: 14 }}>
                        <Heading3>Trusted sending name (DKIM)</Heading3>
                        <SectionIntro lighter>
                            Microsoft 365 Advanced delivery needs a domain name
                            to trust. Issue one here, then paste it into
                            Phishing simulations together with the sending IPs
                            from the selected profile. Without this, Defender
                            may quarantine AwarePHISH mail.
                        </SectionIntro>
                        <FieldLabel>Current name</FieldLabel>
                        <TextDefault bold style={{ display: 'block' }}>
                            {(delivery && delivery.dkimHostname) ||
                                'Not issued yet'}
                        </TextDefault>
                        {delivery && delivery.dkimAvailable ? (
                            <FlexDiv gap={12} style={{ marginTop: 12 }}>
                                <ThemedButton
                                    text={
                                        savingAsset
                                            ? 'Working…'
                                            : delivery.dkimHostname
                                              ? 'Re-issue trusted name'
                                              : 'Issue trusted name'
                                    }
                                    mode="secondary"
                                    disabled={savingAsset}
                                    onClick={async () => {
                                        try {
                                            const r =
                                                await awarePhishStore.issueDkim(
                                                    companyId
                                                );
                                            commonStore.success(
                                                `Ready: ${
                                                    (r &&
                                                        r.issuedDkimHostname) ||
                                                    (r && r.dkimHostname) ||
                                                    'issued'
                                                }`
                                            );
                                            await reload();
                                        } catch (e) {
                                            commonStore.error(
                                                awarePhishStore.error ||
                                                    'Could not issue trusted sending name'
                                            );
                                        }
                                    }}
                                    $noWrap
                                />
                            </FlexDiv>
                        ) : !(delivery && delivery.dkimHostname) ? (
                            <TextTiny
                                lighter
                                style={{ display: 'block', marginTop: 8 }}
                            >
                                Not available yet for this organisation. AwareGO
                                will enable it when your delivery is ready.
                            </TextTiny>
                        ) : null}
                    </Block>

                    <Block padding="20" column>
                        <Heading3>Sync reported phishing</Heading3>
                        <SectionIntro lighter>
                            Pulls recent messages from the reporting mailbox
                            above and marks matching simulation recipients as
                            Reported. Microsoft 365 only.
                        </SectionIntro>
                        <ThemedButton
                            text={
                                savingAsset ? 'Syncing…' : 'Sync reports now'
                            }
                            mode="primary"
                            disabled={
                                savingAsset ||
                                !(delivery && delivery.reportingMailbox)
                            }
                            onClick={async () => {
                                try {
                                    const r =
                                        await awarePhishStore.syncReports(
                                            companyId
                                        );
                                    commonStore.success(
                                        `Scanned ${(r && r.scanned) || 0}, reported ${(r && r.reported) || 0}, skipped ${(r && r.skipped) || 0}`
                                    );
                                } catch (e) {
                                    commonStore.error(
                                        awarePhishStore.error ||
                                            'Report sync failed'
                                    );
                                }
                            }}
                            $noWrap
                        />
                    </Block>
                </>
            )}
        </Container>
    );
}

export default observer(AwarePhishDelivery);
